import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getMyBicMembership,
  selectBicChapter,
  getBicConfiguration,
  getBicChapters,
  getBicCities,
  getBicPods,
  getBicMyPod,
  getBicMyChapter,
  getBicSessions,
  createBicPaymentLink,
  getBicPaymentStatus,
  listBicReferrals,
  getBicReferralCouponBalance,
  getBicSessionCreditBalance,
  listCrossChapterReferrals,
  getMyBicSessions,
  registerForBicSession,
  getBicSessionPaymentStatus,
  requestBicSessionCredit,
  getBicSessionContent,
  getBicMissedSummary,
  submitBicMemberQuery,
} from "../../services/bic_api";

// --- Membership ---

export const fetchBicMembership = createAsyncThunk("bic/fetchBicMembership", async () => {
  const response = await getMyBicMembership();
  return response.response; // { is_member, membership }
});

// Same call as fetchBicMembership, but for the "re-check right before starting checkout" moment —
// deliberately doesn't touch isLoadingMembership, since that flag swaps the whole page for the
// loading placeholder; this should only show a spinner on the button that triggered it.
export const recheckBicMembership = createAsyncThunk("bic/recheckBicMembership", async () => {
  const response = await getMyBicMembership();
  return response.response;
});

export const completeBicChapterJoin = createAsyncThunk(
  "bic/completeBicChapterJoin",
  async (chapter, { rejectWithValue }) => {
    const response = await selectBicChapter(chapter);
    if (response.status !== true || !response.response?.is_member) {
      return rejectWithValue(response.message || "Unable to save your chapter. Please try again.");
    }
    return response.response;
  }
);

// --- Catalog ---

export const fetchBicConfiguration = createAsyncThunk("bic/fetchBicConfiguration", async () => {
  const response = await getBicConfiguration();
  return response.response?.configuration || {};
});

export const fetchBicChapters = createAsyncThunk("bic/fetchBicChapters", async () => {
  const response = await getBicChapters();
  return response.response?.chapters || [];
});

export const fetchBicCities = createAsyncThunk("bic/fetchBicCities", async () => {
  const response = await getBicCities();
  return response.response?.cities || [];
});

export const fetchBicPods = createAsyncThunk("bic/fetchBicPods", async (chapterRefId) => {
  const response = await getBicPods(chapterRefId);
  return response.response?.pods || [];
});

export const fetchBicMyPod = createAsyncThunk("bic/fetchBicMyPod", async () => {
  const response = await getBicMyPod();
  return response.response?.pod || null;
});

// Which chapter this member belongs to, including its calendar PDF link — backs the dashboard's
// "Chapter calendar" card, which only renders once `calendar_link` is set.
export const fetchBicMyChapter = createAsyncThunk("bic/fetchBicMyChapter", async () => {
  const response = await getBicMyChapter();
  return response.response?.chapter || null;
});

export const fetchBicSessions = createAsyncThunk("bic/fetchBicSessions", async (chapterRefId) => {
  const response = await getBicSessions({ chapterRefId, pageSize: 20 });
  return response.response?.sessions || [];
});

/** Fetches configuration + chapters + cities + pods + my-pod + sessions together — mirrors the
 * mobile app's loadBicCatalog(), run once on first BIC visit and again after a chapter change. */
export const fetchBicCatalog = createAsyncThunk(
  "bic/fetchBicCatalog",
  async (chapterRefId, { dispatch }) => {
    await dispatch(fetchBicConfiguration());
    const [chapters, cities] = await Promise.all([
      dispatch(fetchBicChapters()).unwrap(),
      dispatch(fetchBicCities()).unwrap(),
    ]);
    await Promise.all([
      dispatch(fetchBicPods(chapterRefId)),
      dispatch(fetchBicMyPod()),
      dispatch(fetchBicMyChapter()),
      dispatch(fetchBicSessions(chapterRefId)),
    ]);
    return { chapters, cities };
  }
);

// --- Payment ---

export const createBicPaymentLinkThunk = createAsyncThunk(
  "bic/createBicPaymentLink",
  async (applyReferralDiscount, { rejectWithValue }) => {
    const response = await createBicPaymentLink(applyReferralDiscount);
    if (response.status !== true) {
      return rejectWithValue(response.message || "Unable to start payment. Please try again.");
    }
    return response.response; // { payment_url, amount, membership_id, billing_mode }
  }
);

export const resolveBicPaymentStatus = createAsyncThunk(
  "bic/resolveBicPaymentStatus",
  async (refId) => {
    const response = await getBicPaymentStatus(refId);
    return response.response; // { is_member, membership }
  }
);

// --- Sessions (my registrations — upcoming/attended/missed, distinct from the plain catalog
// fetch above which only feeds the dashboard's "next session" card) ---

export const fetchMyBicSessions = createAsyncThunk("bic/fetchMyBicSessions", async () => {
  const response = await getMyBicSessions();
  return response.response; // { upcoming, attended, missed }
});

export const registerForBicSessionThunk = createAsyncThunk(
  "bic/registerForBicSession",
  async ({ sessionRefId, chapterRefId, useSessionCredit }, { rejectWithValue }) => {
    const response = await registerForBicSession({ sessionRefId, chapterRefId, useSessionCredit });
    if (response.status !== true) {
      return rejectWithValue(response.message || "Unable to register. Please try again.");
    }
    return response.response; // { registration, payment_url }
  }
);

export const resolveBicSessionPaymentThunk = createAsyncThunk(
  "bic/resolveBicSessionPayment",
  async (refId) => {
    const response = await getBicSessionPaymentStatus(refId);
    return response.response?.registration || null;
  }
);

export const requestBicSessionCreditThunk = createAsyncThunk(
  "bic/requestBicSessionCredit",
  async (sessionRefId, { rejectWithValue }) => {
    const response = await requestBicSessionCredit(sessionRefId);
    if (response.status !== true) {
      return rejectWithValue(response.message || "Unable to request credit. Please try again.");
    }
    return response.response?.registration || null;
  }
);

// Attended-tab session content (sketch notes/summary/recording)
export const fetchBicSessionContent = createAsyncThunk(
  "bic/fetchBicSessionContent",
  async ({ sessionRefId }, { rejectWithValue }) => {
    const response = await getBicSessionContent({ session_ref_id: sessionRefId });
    if (response.status !== true) {
      return rejectWithValue(response.message || "Unable to load session content.");
    }
    return response.response; // { items: [...] }
  }
);

// Missed-tab breakdown + raise-a-query
export const fetchBicMissedSummary = createAsyncThunk(
  "bic/fetchBicMissedSummary",
  async (_, { rejectWithValue }) => {
    const response = await getBicMissedSummary();
    if (response.status !== true) {
      return rejectWithValue(response.message || "Unable to load missed sessions.");
    }
    return response.response; // { total_since_joined, invited_not_registered, registered_not_attended, missed_sessions }
  }
);

export const submitMemberQueryThunk = createAsyncThunk(
  "bic/submitMemberQuery",
  async ({ sessionRefId, reason, details }, { rejectWithValue }) => {
    const response = await submitBicMemberQuery({ session_ref_id: sessionRefId, reason, details });
    if (response.status !== true) {
      return rejectWithValue(response.message || "Unable to submit your query. Please try again.");
    }
    return response.response; // { request }
  }
);

// --- Referrals ---

export const fetchBicReferralData = createAsyncThunk("bic/fetchBicReferralData", async () => {
  const [all, converted, coupon, crossChapter] = await Promise.all([
    listBicReferrals({ limit: 100 }),
    listBicReferrals({ status: "converted", limit: 1 }),
    getBicReferralCouponBalance(),
    listCrossChapterReferrals({ limit: 50 }),
  ]);
  return {
    referrals: all.response?.referrals || [],
    referralsSubmitted: all.response?.total || 0,
    qualifiedReferrals: converted.response?.total || 0,
    couponActiveMonths: coupon.response?.active_months || 0,
    // Approved cross-chapter requests have already become a real referral (shown in the normal
    // list instead) — showing them here too would be a confusing duplicate.
    crossChapterRequests: (crossChapter.response?.requests || []).filter(
      (r) => !(r.state === "resolved" && r.outcome === "approved")
    ),
  };
});

// --- Session credit (D8) ---

export const fetchBicSessionCredit = createAsyncThunk("bic/fetchBicSessionCredit", async () => {
  const result = await getBicSessionCreditBalance();
  return {
    balance: result.response?.balance || 0,
    items: result.response?.items || [],
  };
});

const initialState = {
  // membership
  latestMembership: null,
  isMember: false,
  isLoadingMembership: false,
  isSavingChapter: false,
  chapterJoinError: null,

  // catalog
  configuration: null,
  apiChapters: [],
  apiCities: [],
  pods: [],
  myPod: null,
  myChapter: null,
  sessions: [],
  isLoadingCatalog: false,
  isLoadingChapters: false,
  mySessions: { upcoming: [], attended: [], missed: [] },
  isLoadingMySessions: false,
  sessionContent: { items: [] },
  isLoadingSessionContent: false,
  missedSummary: null,
  isLoadingMissedSummary: false,
  isSubmittingQuery: false,

  // payment
  isProcessingPayment: false,
  isResolvingPayment: false,
  resolveAttempt: 0,
  paymentErrorMessage: null,

  // chapter-selection / review-and-pay local-ish shared UI state
  selectedChapter: null,
  businessLocation: "",
  isReferralDiscountApplied: false,

  // referrals
  bicReferrals: [],
  crossChapterRequests: [],
  referralsSubmitted: 0,
  qualifiedReferrals: 0,
  couponActiveMonths: 0,
  isLoadingReferralData: false,

  // session credit (D8)
  sessionCreditBalance: 0,
  sessionCreditItems: [],
  isLoadingSessionCredit: false,
};

const bicSlice = createSlice({
  name: "bic",
  initialState,
  reducers: {
    setSelectedChapter: (state, action) => {
      state.selectedChapter = action.payload;
    },
    setBusinessLocation: (state, action) => {
      state.businessLocation = action.payload;
    },
    applyReferralDiscount: (state) => {
      state.isReferralDiscountApplied = true;
    },
    removeReferralDiscount: (state) => {
      state.isReferralDiscountApplied = false;
    },
    clearPaymentError: (state) => {
      state.paymentErrorMessage = null;
    },
    resetForNewPaymentAttempt: (state) => {
      state.latestMembership = null;
      state.isMember = false;
      state.paymentErrorMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Membership
      .addCase(fetchBicMembership.pending, (state) => {
        state.isLoadingMembership = true;
      })
      .addCase(fetchBicMembership.fulfilled, (state, action) => {
        state.isLoadingMembership = false;
        applyMembership(state, action.payload);
      })
      .addCase(fetchBicMembership.rejected, (state) => {
        state.isLoadingMembership = false;
      })
      .addCase(recheckBicMembership.fulfilled, (state, action) => {
        applyMembership(state, action.payload);
      })

      .addCase(completeBicChapterJoin.pending, (state) => {
        state.isSavingChapter = true;
        state.chapterJoinError = null;
      })
      .addCase(completeBicChapterJoin.fulfilled, (state, action) => {
        state.isSavingChapter = false;
        applyMembership(state, action.payload);
      })
      .addCase(completeBicChapterJoin.rejected, (state, action) => {
        state.isSavingChapter = false;
        state.chapterJoinError = action.payload || "Unable to save your chapter. Please try again.";
      })

      // Catalog
      .addCase(fetchBicConfiguration.fulfilled, (state, action) => {
        state.configuration = action.payload;
      })
      .addCase(fetchBicChapters.pending, (state) => {
        // Only show the chapter-picker loader on the first fetch — background refetches must not
        // flip isLoadingChapters back on and flash the skeleton over an already-rendered grid.
        if (state.apiChapters.length === 0) {
          state.isLoadingChapters = true;
        }
      })
      .addCase(fetchBicChapters.fulfilled, (state, action) => {
        state.isLoadingChapters = false;
        state.apiChapters = action.payload;
      })
      .addCase(fetchBicChapters.rejected, (state) => {
        state.isLoadingChapters = false;
      })
      .addCase(fetchBicCities.fulfilled, (state, action) => {
        state.apiCities = action.payload;
      })
      .addCase(fetchBicPods.fulfilled, (state, action) => {
        state.pods = action.payload;
      })
      .addCase(fetchBicMyPod.fulfilled, (state, action) => {
        state.myPod = action.payload;
      })
      .addCase(fetchBicMyChapter.fulfilled, (state, action) => {
        state.myChapter = action.payload;
      })
      .addCase(fetchBicSessions.fulfilled, (state, action) => {
        state.sessions = action.payload;
      })
      .addCase(fetchBicCatalog.pending, (state) => {
        state.isLoadingCatalog = true;
      })
      .addCase(fetchBicCatalog.fulfilled, (state) => {
        state.isLoadingCatalog = false;
      })
      .addCase(fetchBicCatalog.rejected, (state) => {
        state.isLoadingCatalog = false;
      })

      // Payment
      .addCase(createBicPaymentLinkThunk.pending, (state) => {
        state.isProcessingPayment = true;
        state.paymentErrorMessage = null;
      })
      .addCase(createBicPaymentLinkThunk.fulfilled, (state) => {
        state.isProcessingPayment = false;
      })
      .addCase(createBicPaymentLinkThunk.rejected, (state, action) => {
        state.isProcessingPayment = false;
        state.paymentErrorMessage = action.payload || "Unable to start payment. Please try again.";
      })
      .addCase(resolveBicPaymentStatus.fulfilled, (state, action) => {
        applyMembership(state, action.payload);
      })

      // My sessions
      .addCase(fetchMyBicSessions.pending, (state) => {
        state.isLoadingMySessions = true;
      })
      .addCase(fetchMyBicSessions.fulfilled, (state, action) => {
        state.isLoadingMySessions = false;
        const payload = action.payload || {};
        state.mySessions = {
          upcoming: payload.upcoming || [],
          attended: payload.attended || [],
          missed: payload.missed || [],
        };
      })
      .addCase(fetchMyBicSessions.rejected, (state) => {
        state.isLoadingMySessions = false;
      })

      // Session content (Attended tab)
      .addCase(fetchBicSessionContent.pending, (state) => {
        state.isLoadingSessionContent = true;
      })
      .addCase(fetchBicSessionContent.fulfilled, (state, action) => {
        state.isLoadingSessionContent = false;
        state.sessionContent = action.payload || { items: [] };
      })
      .addCase(fetchBicSessionContent.rejected, (state) => {
        state.isLoadingSessionContent = false;
      })

      // Missed summary + raise-a-query
      .addCase(fetchBicMissedSummary.pending, (state) => {
        state.isLoadingMissedSummary = true;
      })
      .addCase(fetchBicMissedSummary.fulfilled, (state, action) => {
        state.isLoadingMissedSummary = false;
        state.missedSummary = action.payload;
      })
      .addCase(fetchBicMissedSummary.rejected, (state) => {
        state.isLoadingMissedSummary = false;
      })
      .addCase(submitMemberQueryThunk.pending, (state) => {
        state.isSubmittingQuery = true;
      })
      .addCase(submitMemberQueryThunk.fulfilled, (state) => {
        state.isSubmittingQuery = false;
      })
      .addCase(submitMemberQueryThunk.rejected, (state) => {
        state.isSubmittingQuery = false;
      })

      // Referrals
      .addCase(fetchBicReferralData.pending, (state) => {
        state.isLoadingReferralData = true;
      })
      .addCase(fetchBicReferralData.fulfilled, (state, action) => {
        state.isLoadingReferralData = false;
        state.bicReferrals = action.payload.referrals;
        state.referralsSubmitted = action.payload.referralsSubmitted;
        state.qualifiedReferrals = action.payload.qualifiedReferrals;
        state.couponActiveMonths = action.payload.couponActiveMonths;
        state.crossChapterRequests = action.payload.crossChapterRequests;
      })
      .addCase(fetchBicReferralData.rejected, (state) => {
        state.isLoadingReferralData = false;
      })

      // Session credit
      .addCase(fetchBicSessionCredit.pending, (state) => {
        state.isLoadingSessionCredit = true;
      })
      .addCase(fetchBicSessionCredit.fulfilled, (state, action) => {
        state.isLoadingSessionCredit = false;
        state.sessionCreditBalance = action.payload.balance;
        state.sessionCreditItems = action.payload.items;
      })
      .addCase(fetchBicSessionCredit.rejected, (state) => {
        state.isLoadingSessionCredit = false;
      });
  },
});

/** Mirrors BicController._applyMembership — the single place membership + derived display state
 * gets updated from any backend response ({ is_member, membership }). */
function applyMembership(state, payload) {
  const membership = payload?.membership || null;
  state.latestMembership = membership;
  state.isMember = payload?.is_member === true;
}

export const {
  setSelectedChapter,
  setBusinessLocation,
  applyReferralDiscount,
  removeReferralDiscount,
  clearPaymentError,
  resetForNewPaymentAttempt,
} = bicSlice.actions;

export default bicSlice.reducer;
